// app/components/AdvancedChart/components/TradingModal.tsx
import React, { useState, useEffect } from 'react'
import Modal from '../../Modal/Modal'
import { ConnectorManager } from '../../Connectors/ConnectorManager'
import { formatPrice } from '../utils/priceFormatter'

// Import all BuySell components
import ZerodhaBuySell from '../../Connectors/zerodha/BuySell'
import BinanceBuySell from '../../Connectors/binance/BuySell'
import GrowwBuySell from '../../Connectors/groww/BuySell'
import NintradeStocksBuySell from '../../Connectors/nintrade/stocks/BuySell'
import NintradeWeb3BuySell from '../../Connectors/nintrade/web3/BuySell'

interface TradingModalProps {
  isOpen: boolean
  onClose: () => void
  symbol: string
  price: number | null
  action: 'BUY' | 'SELL' | null
}

export default function TradingModal({
  isOpen,
  onClose,
  symbol,
  price,
  action
}: TradingModalProps) {
  const [activeBroker, setActiveBroker] = useState<string>('nintrade')
  
  // Get the active broker on mount
  useEffect(() => {
    const connectorManager = ConnectorManager.getInstance()
    const brokers = connectorManager.getBrokers()
    const enabledBroker = brokers.find(broker => broker.enabled)
    
    if (enabledBroker) {
      setActiveBroker(enabledBroker.id)
    }
  }, [isOpen]) // Re-check when modal opens

  // Format price for display and use
  const formattedPrice = price ? formatPrice(price) : ''
  
  // Determine which BuySell component to render based on active broker
  const renderBuySellComponent = () => {
    // Pass the symbol and price to the BuySell component
    const props = {
      symbol,
      initialPrice: formattedPrice,
      initialAction: action
    }
    
    switch (activeBroker) {
      case 'zerodha':
        return <ZerodhaBuySell {...props} />
      case 'binance':
        return <BinanceBuySell {...props} />
      case 'groww':
        return <GrowwBuySell {...props} />
      case 'nintrade':
        // For nintrade, we need to determine if it's stocks or web3
        // This is a simple check - if symbol contains a dot, it's likely a stock
        if (symbol.includes('.')) {
          return <NintradeStocksBuySell {...props} />
        } else {
          return <NintradeWeb3BuySell {...props} />
        }
      default:
        return <div>No trading connector is enabled. Please enable a connector in the settings.</div>
    }
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title={`${action === 'BUY' ? 'Buy' : 'Sell'} ${symbol} at ${formattedPrice}`}
      width="600px"
    >
      {renderBuySellComponent()}
    </Modal>
  )
}